import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, School } from '@mui/icons-material';
import { EDUCATION_LEVELS, EMPTY_EDUCATION } from './fieldConfig';
import DetailField from './DetailField';

// Repeatable list of school / college / university records for a person
const EducationSection = ({ value = [], onChange, errors = {}, readOnly = false }) => {
  const updateEntry = (index, field, fieldValue) => {
    onChange(value.map((entry, i) => (i === index ? { ...entry, [field]: fieldValue } : entry)));
  };

  const addEntry = () => onChange([...value, { ...EMPTY_EDUCATION }]);
  const removeEntry = (index) => onChange(value.filter((_, i) => i !== index));

  const fieldError = (index, field) => errors[`education.${index}.${field}`];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
        >
          <School sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
          Education
        </Typography>
        {!readOnly && value.length > 0 && (
          <Button size="small" startIcon={<Add />} onClick={addEntry} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Add another
          </Button>
        )}
      </Stack>

      {value.length === 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', borderColor: '#cbd5e1', bgcolor: '#f8fafc' }}
        >
          <Typography variant="body2" sx={{ color: '#64748b', mb: readOnly ? 0 : 1.5 }}>
            {readOnly ? 'No education details added' : 'Add school, college or university details'}
          </Typography>
          {!readOnly && (
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={addEntry} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Add education
            </Button>
          )}
        </Paper>
      )}

      <Stack spacing={2}>
        {value.map((entry, index) => (
          <Paper key={index} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, borderColor: '#e2e8f0' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                {entry.level || `Education ${index + 1}`}
                {entry.institution ? ` · ${entry.institution}` : ''}
              </Typography>
              {!readOnly && (
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeEntry(index)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  select
                  size="small"
                  label="Level"
                  value={entry.level}
                  onChange={(e) => updateEntry(index, 'level', e.target.value)}
                >
                  {EDUCATION_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </DetailField>
              </Grid>
              <Grid item xs={12} md={8}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="School / College / University Name"
                  value={entry.institution}
                  onChange={(e) => updateEntry(index, 'institution', e.target.value)}
                  error={!!fieldError(index, 'institution')}
                  helperText={fieldError(index, 'institution')}
                  placeholder="e.g., St. Joseph's College, Bangalore"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="Board / University"
                  value={entry.boardOrUniversity}
                  onChange={(e) => updateEntry(index, 'boardOrUniversity', e.target.value)}
                  placeholder="e.g., CBSE, Bangalore University"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="Course / Degree"
                  value={entry.course}
                  onChange={(e) => updateEntry(index, 'course', e.target.value)}
                  placeholder="e.g., B.Com, B.Tech, MBA"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="Field of Study / Stream"
                  value={entry.fieldOfStudy}
                  onChange={(e) => updateEntry(index, 'fieldOfStudy', e.target.value)}
                  placeholder="e.g., Computer Science, Science (PCMB)"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="Start Year"
                  value={entry.startYear}
                  onChange={(e) => updateEntry(index, 'startYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  error={!!fieldError(index, 'startYear')}
                  helperText={fieldError(index, 'startYear')}
                  inputProps={{ inputMode: 'numeric' }}
                  placeholder="e.g., 2016"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label={entry.currentlyStudying ? 'End Year (ongoing)' : 'End / Passing Year'}
                  value={entry.currentlyStudying ? '' : entry.endYear}
                  onChange={(e) => updateEntry(index, 'endYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  disabled={!readOnly && entry.currentlyStudying}
                  error={!!fieldError(index, 'endYear')}
                  helperText={fieldError(index, 'endYear')}
                  inputProps={{ inputMode: 'numeric' }}
                  placeholder="e.g., 2020"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailField
                  readOnly={readOnly}
                  fullWidth
                  size="small"
                  label="Grade / % / CGPA"
                  value={entry.grade}
                  onChange={(e) => updateEntry(index, 'grade', e.target.value)}
                  placeholder="e.g., 85% or 8.2 CGPA"
                />
              </Grid>
              <Grid item xs={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!entry.currentlyStudying}
                      onChange={(e) => updateEntry(index, 'currentlyStudying', e.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label={<Typography variant="body2">Currently studying</Typography>}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default EducationSection;
